/** @format */
import { MarketCode } from '@toucan/config/markets';

type PriceModifier = {
    id: string;
    modelId: number;
    present: boolean;
    value: number;
};

type ItemObservation = {
    id: string;
    observationId: number;
    tierId: number;
    adjustment: number;
};

enum AttributeKey {
    UNDEFINED_REASON = 'UNDEFINED_REASON',
    SHUTTER_COUNT = 'SHUTTER_COUNT',
    BATTERY_CHARGE_COUNT = 'BATTERY_CHARGE_COUNT',
    FIRMWARE = 'FIRMWARE',
    HOUR_COUNT = 'HOUR_COUNT',
}

type Attribute = {
    id: string;
    key: AttributeKey;
    content: string;
    value: number;
};

type Adjustment = {
    adjustmentType: string;
    description: string;
    id: string;
    taxInclusive: boolean;
    // TODO: Populate type
    taxItems: any[];
    value: number;
};

type Address = {
    id?: number;
    addressee: string;
    line1: string;
    line2: string;
    town: string;
    county?: string;
    state?: string;
    postcode?: string;
    zipcode?: string;
    country: string;
    deliveryDefault?: boolean;
    billingDefault?: boolean;
    saveToAddressBook?: boolean;
    removeFromAddressBook?: boolean;
    nickname?: string;
    verified?: boolean;
};

export enum CalcServiceLineItemType {
    GOODS_IN = 'GOODS_IN',
    GOODS_OUT = 'GOODS_OUT',
    CHARGE = 'CHARGE',
    CHARGE_PART_REFUND = 'CHARGE_PART_REFUND',
    RETURN = 'RETURN',
    WARRANTY = 'WARRANTY',
    WARRANTY_REFUND = 'WARRANTY_REFUND',
}

export enum CalcServicePromoType {
    UNDEFINED = 'UNDEFINED',
    DISCOUNT = 'DISCOUNT',
    BONUS = 'BONUS',
    SHIPMENT = 'SHIPMENT',
    COLLECTION = 'COLLECTION',
}

export enum TaxItemType {
    UNDEFINED_TYPE = 'UNDEFINED_TYPE',
    SALES_TAX = 'SALES_TAX',
    PURCHASE_TAX = 'PURCHASE_TAX',
    DUTY = 'DUTY',
}

export type CalcServicePromotionRate = {
    type: string;
    value: string;
};

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalcServicePromotion {
    code: string;
    type: CalcServicePromoType;
    rate: CalcServicePromotionRate;
    value: number;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalcServiceAdjustment {
    type: string;
    value: number;
}

export type CalcServiceTotalsKeys = 'totalIncTax' | 'totalExTax' | 'totalTax';
export type FormattedTotalKeys =
    | CalcServiceTotalsKeys
    | 'total'
    | 'sellingTotal'
    | 'buyingTotal'
    | 'deliveryTotal'
    | 'totalLabel';

export type CalcServiceTotals = Record<CalcServiceTotalsKeys, number>;
export type FormattedCalcServiceTotals = Record<FormattedTotalKeys, string>;

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalcServiceTaxItem {
    type: TaxItemType;
    rate: string;
    value: number;
    scheme: string;
    itemizable: boolean;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalcServiceLineItem {
    productId: string;
    type: CalcServiceLineItemType;
    value: number;
    adjustments: CalcServiceAdjustment[];
    taxItems: CalcServiceTaxItem[];
    subTotals: CalcServiceTotals;
    taxInclusive: boolean;
    remoteReference: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalcServiceCustomerDetails {
    emailAddress?: string;
    address?: Address;
    taxExemptionRef?: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalcServiceTaxCalculationInformation {
    market: MarketCode;
    id?: string;
    customerId: number;
    parentCalculationDate?: string;
    refundableTax?: number;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalcServiceMetadata {
    containsNonItemizableTaxes: boolean;
    containsItemizableTaxes: boolean;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalcServiceResponse {
    lineItems: CalcServiceLineItem[];
    promotions: CalcServicePromotion[];
    totals: CalcServiceTotals;
    taxes?: CalcServiceTaxItem[];
    meta?: CalcServiceMetadata;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalcServiceItemObservations
    extends Omit<ItemObservation, 'adjustment' | 'id'> {
    value: number;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface CalcServiceItemAdjustments
    extends Omit<Adjustment, 'adjustmentType'> {
    type: string;
}

export type CalcServiceRequestItem = {
    type: CalcServiceLineItemType;
    baseValue: number;
    productId?: number;
    attributes?: Attribute[];
    observations?: CalcServiceItemObservations[];
    adjustments?: Adjustment[];
    priceModifiers?: PriceModifier[];
};

export type CalcServiceRequest = {
    lineItems: CalcServiceRequestItem[];
    promotions: { code: string }[];
    customerDetails: CalcServiceCustomerDetails;
    taxCalculationInformation: CalcServiceTaxCalculationInformation;
};
